// Training data layer — the course player's spine.
//
// Three concerns, kept apart:
//  1. PURE check mechanics (unit-tested without a DB): the three-attempt rule.
//     A wrong answer gets a nudge and another try; the correct answer (with its
//     explanation) is revealed only after the THIRD miss — or immediately on a
//     correct pick, so the *why* lands either way. A question missed three
//     times is FLAGGED: it must reappear in the nurse's certification exam.
//  2. Loaders (envelope, never throw): catalogue + questions for everyone;
//     the signed-in nurse's own progress/answers (RLS: *_read_own).
//  3. Best-effort writers: watch events and answer attempts. A failed
//     analytics write must NEVER interrupt playback or a check — the nurse's
//     learning outranks our telemetry — so writers log and carry on.
//
// Admin aggregations (per-nurse, per-question) are pure and live here too, so
// the Reports panel and the tests share one implementation.

import { getClient, ok, fail, fetchAll } from '../db/client.js';
import { TABLES, BUCKETS } from '../db/contract.js';
import { store, SLICES } from '../state/store.js';
import { SUPABASE_URL } from '../db/creds.js';

// ─── Pure: mojibake repair ─────────────────────────────────────────────────
//
// The catalogue text reached the database through a Windows-1252 clipboard hop
// (PowerShell 5.1's Get-Content reads a UTF-8 file as cp1252 unless told
// otherwise), so every em dash landed as 'â€"' and every curly quote as 'â€™'.
// The 96 pack repairs the stored rows; this repairs what is ALREADY stored, so
// a nurse never reads mojibake while a migration is pending — and so a future
// careless paste can never put it back on screen.
//
// The repair is the exact inverse of the corruption: map each character back to
// the cp1252 byte it came from, then decode those bytes as UTF-8. Anything that
// does not round-trip cleanly is left completely alone — a title with a real
// 'â' in it must survive untouched.

/** cp1252's 0x80-0x9F block: the Unicode code point each byte decodes to. */
const CP1252_HIGH = Object.freeze({
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85,
  0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a,
  0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92,
  0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c,
  0x017e: 0x9e, 0x0178: 0x9f,
});

// The tell-tale of a cp1252 misread: a UTF-8 lead byte (C2/C3/E2, which cp1252
// renders as Â/Ã/â) followed by a continuation byte, which lands in the
// -¿ range or on one of cp1252's punctuation glyphs. Written as escapes on
// purpose: the literal characters include a non-breaking space, invisible in an
// editor and a silent trap for the next person to touch this line.
const MOJIBAKE_RE = /[ÂÃâ][-¿€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ]/;

/**
 * Repair text corrupted by a UTF-8 → cp1252 misread. Pure, and safe on text
 * that was never corrupted: anything that does not decode cleanly as UTF-8 is
 * returned exactly as given.
 * @param {any} s
 * @returns {any} the repaired string, or the input unchanged
 */
export function deMojibake(s) {
  if (typeof s !== 'string' || !s || !MOJIBAKE_RE.test(s)) return s;
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i += 1) {
    const cp = s.codePointAt(i);
    if (cp === undefined) return s;
    if (cp <= 0xff) bytes[i] = cp;
    else if (cp in CP1252_HIGH) bytes[i] = CP1252_HIGH[cp];
    else return s; // not a cp1252 round-trip — leave it alone
  }
  const TD = /** @type {any} */ (globalThis).TextDecoder;
  if (!TD) return s;
  try {
    const dec = new TD('utf-8', { fatal: true });
    const out = dec.decode(bytes);
    // A repair that changes nothing, or produces a replacement char, is no repair.
    return out.indexOf('�') === -1 ? out : s;
  } catch {
    return s;
  }
}

/**
 * Repair every text field of a catalogue/question row. Pure.
 * @param {any} row
 * @param {string[]} fields
 * @returns {any}
 */
function cleanRow(row, fields) {
  if (!row || typeof row !== 'object') return row;
  const out = Object.assign({}, row);
  for (const f of fields) {
    if (typeof out[f] === 'string') out[f] = deMojibake(out[f]);
  }
  if (Array.isArray(out.options)) out.options = out.options.map(deMojibake);
  return out;
}

/** @param {any[]} rows @returns {any[]} */
export function cleanVideos(rows) {
  return (rows || []).map((r) => cleanRow(r, ['title', 'process_tag']));
}

/** @param {any[]} rows @returns {any[]} */
export function cleanQuestions(rows) {
  return (rows || []).map((r) => cleanRow(r, ['prompt', 'explanation']));
}

// ─── Pure: the course spine — chapters and their order ─────────────────────
//
// A flat list of 24 parts reads as a pile, not a course. The chapters below are
// the teaching order: each one answers a question the next one builds on. The
// order lives HERE, keyed by the stable video_id, rather than in the ordinal
// column — so the course can be re-sequenced without a migration, and the
// numbers a nurse sees always match the sequence she is actually walking.

export const CHAPTERS = Object.freeze([
  Object.freeze({
    key: 'start',
    title: 'Getting started',
    blurb: 'What the programme is for, how to sign in, and how to read your day.',
    videos: Object.freeze(['m01', 'm02', 'm03']),
  }),
  Object.freeze({
    key: 'records',
    title: 'Patients and their records',
    blurb: 'Find a patient, read every panel of the record, and bring new patients in.',
    videos: Object.freeze(['m10', 'm04', 'm05', 'm12']),
  }),
  Object.freeze({
    key: 'stages',
    title: 'The eight stages',
    blurb: 'The whole patient journey, stage by stage — what you do, log and move.',
    videos: Object.freeze(['m06', 'm07']),
  }),
  Object.freeze({
    key: 'duties',
    title: 'The daily duties',
    blurb: 'Money, safety and reporting — the three duties that run alongside every stage.',
    videos: Object.freeze(['m08', 'm09', 'm11']),
  }),
  Object.freeze({
    key: 'together',
    title: 'Putting it together',
    blurb: 'One patient end to end, the professional standard, and the shape of your day.',
    videos: Object.freeze(['m13', 'm14', 'm15']),
  }),
  Object.freeze({
    key: 'clinics',
    title: 'Scenario clinics',
    blurb: 'Eight situations you will meet. Watch what a good nurse does, then do it.',
    videos: Object.freeze(['sc1', 'sc2', 'sc3', 'sc4', 'sc5', 'sc6', 'sc7', 'sc8']),
  }),
  // Admin-tier only. A nurse is not shown this chapter at all — not a locked
  // row, not a greyed card: it is absent, and her course totals count 23 parts,
  // because a course that advertises something you cannot open is worse than
  // one that never mentions it.
  //
  // This is a PRESENTATION gate, not a security boundary. The catalogue row is
  // world-readable to signed-in staff (tv_read) and the training bucket is
  // public, so the file is still reachable by anyone who knows its URL. If the
  // appendix must be genuinely restricted, the bucket has to go private and
  // every part move to signed URLs — say so before assuming this hides it.
  Object.freeze({
    key: 'admin',
    title: 'For administrators',
    blurb: 'The Admin console — accounts, roles and the settings behind the programme.',
    videos: Object.freeze(['a1']),
    adminOnly: true,
  }),
]);

/** Object.freeze narrows the literals, so read the optional flag through a cast.
 * @param {any} c @returns {boolean} */
function chapterIsAdminOnly(c) {
  return !!(c && c.adminOnly);
}

/**
 * Teaching order as a flat list of video_ids.
 * @param {{isAdmin?: boolean}} [ctx] omit, or pass isAdmin:false, to exclude
 *   the admin-only chapters exactly as a nurse's course excludes them.
 * @returns {string[]}
 */
export function courseOrder(ctx) {
  const admin = !!(ctx && ctx.isAdmin);
  return CHAPTERS.filter((c) => admin || !chapterIsAdminOnly(c)).flatMap((c) => c.videos.slice());
}

/**
 * The course as chapters, in teaching order, with each part's number and state.
 * Pure. Parts the chapters do not name (anything added later) are appended in
 * ordinal order under a final chapter, so nothing can ever go missing from the
 * screen just because this file was not updated.
 * @param {any[]} videos
 * @param {Record<string, any>} progress
 * @param {{isAdmin?: boolean}} [ctx] admin-only chapters are omitted entirely
 *   unless isAdmin — and the numbering closes up behind them, so a nurse's
 *   course runs 1..23 with no gap where the appendix used to be.
 * @returns {Array<{key: string, title: string, blurb: string, parts: any[], done: number, total: number, percent: number, seconds: number}>}
 */
export function courseOutline(videos, progress, ctx) {
  const isAdminCtx = !!(ctx && ctx.isAdmin);
  const byId = new Map((videos || []).filter((v) => v && v.video_id).map((v) => [v.video_id, v]));
  const claimed = new Set();
  let n = 0;
  /** @type {Array<{key: string, title: string, blurb: string, parts: any[]}>} */
  const chapters = CHAPTERS.filter((c) => isAdminCtx || !chapterIsAdminOnly(c)).map((c) => {
    const parts = [];
    for (const id of c.videos) {
      const v = byId.get(id);
      if (!v) continue;
      claimed.add(id);
      n += 1;
      parts.push(Object.assign({}, v, { number: n, chapter: c.title, chapterKey: c.key }));
    }
    return { key: c.key, title: c.title, blurb: c.blurb, parts };
  });
  const hidden = new Set(
    CHAPTERS.filter((c) => chapterIsAdminOnly(c) && !isAdminCtx).flatMap((c) => c.videos)
  );
  const extras = (videos || [])
    .filter((v) => v && v.video_id && !claimed.has(v.video_id) && !hidden.has(v.video_id))
    .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0))
    .map((v) => {
      n += 1;
      return Object.assign({}, v, { number: n, chapter: 'More parts', chapterKey: 'more' });
    });
  if (extras.length) {
    chapters.push({ key: 'more', title: 'More parts', blurb: 'Recently published.', parts: extras });
  }
  return chapters
    .filter((c) => c.parts.length)
    .map((c) => {
      const done = c.parts.filter((p) => (progress || {})[p.video_id] && progress[p.video_id].done).length;
      return Object.assign(c, {
        done,
        total: c.parts.length,
        percent: c.parts.length ? Math.round((done / c.parts.length) * 100) : 0,
        seconds: c.parts.reduce((s, p) => s + (Number(p.duration_seconds) || 0), 0),
      });
    });
}

/**
 * Course-level totals plus the part to resume on: the first one not finished,
 * preferring one already in progress. Pure.
 * @param {Array<{parts: any[]}>} outline
 * @param {Record<string, any>} progress
 * @returns {{done: number, total: number, percent: number, seconds: number, resume: any|null, resumeIsNew: boolean}}
 */
export function courseSummary(outline, progress) {
  const parts = (outline || []).flatMap((c) => c.parts);
  const p = progress || {};
  const done = parts.filter((x) => p[x.video_id] && p[x.video_id].done).length;
  const unfinished = parts.filter((x) => !(p[x.video_id] && p[x.video_id].done));
  const inFlight = unfinished.find((x) => p[x.video_id] && p[x.video_id].started);
  const resume = inFlight || unfinished[0] || null;
  return {
    done,
    total: parts.length,
    percent: parts.length ? Math.round((done / parts.length) * 100) : 0,
    seconds: parts.reduce((s, x) => s + (Number(x.duration_seconds) || 0), 0),
    resume,
    resumeIsNew: !inFlight && done === 0,
  };
}

// ─── Pure: the three-attempt check mechanic ────────────────────────────────

export const MAX_ATTEMPTS = 3;

// Professional nudges — encouraging, never jokey, and never leaking the answer.
const NUDGES = [
  'Not quite — think about what the patient needs first, and try again.',
  'Still not it — picture yourself on the actual screen. One more try.',
];

/**
 * Outcome of one answer attempt. Pure.
 * @param {{correct_index?: number, explanation?: string}} question
 * @param {number} selectedIndex 0-based option the nurse picked
 * @param {number} attemptNo 1-based attempt number for THIS question
 * @returns {{correct: boolean, done: boolean, reveal: boolean, flagged: boolean, nudge: string|null, explanation: string|null}}
 *  - done: this question is finished (right answer, or attempts exhausted)
 *  - reveal: show the correct option now
 *  - flagged: missed three times — resurface it in the certification exam
 */
export function answerOutcome(question, selectedIndex, attemptNo) {
  const q = question || {};
  const correct = Number(selectedIndex) === Number(q.correct_index);
  if (correct) {
    return { correct: true, done: true, reveal: true, flagged: false, nudge: null, explanation: q.explanation || null };
  }
  if (attemptNo >= MAX_ATTEMPTS) {
    return { correct: false, done: true, reveal: true, flagged: true, nudge: null, explanation: q.explanation || null };
  }
  return {
    correct: false,
    done: false,
    reveal: false,
    flagged: false,
    nudge: NUDGES[Math.min(attemptNo - 1, NUDGES.length - 1)],
    explanation: null,
  };
}

/**
 * The questions for one video, in order. Pure.
 * @param {any[]} questions
 * @param {string} videoId
 * @returns {any[]}
 */
export function questionsForVideo(questions, videoId) {
  return (questions || [])
    .filter((q) => q && q.video_id === videoId && q.active !== false)
    .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
}

/**
 * The questions still OUTSTANDING for this video — the ones with no finished
 * answer yet. Pure.
 *
 * A check that restarts from question one every time punishes a nurse for being
 * interrupted, and re-asks things she has already answered correctly. Resuming
 * where she left off is both kinder and more honest about what is left.
 * @param {any[]} questions
 * @param {string} videoId
 * @param {any[]} answers the nurse's own rows
 * @returns {any[]}
 */
export function outstandingQuestions(questions, videoId, answers) {
  return questionsForVideo(questions, videoId).filter(
    (q) =>
      !(answers || []).some(
        (a) => a.question_id === q.question_id && (a.is_correct || a.attempt_no >= MAX_ATTEMPTS)
      )
  );
}

/** Watch events that prove the nurse actually started this part. */
const STARTED_EVENTS = new Set(['play', 'heartbeat', 'pause', 'seek', 'complete', 'check_start']);

/**
 * How close to the end counts as having watched it. The 'ended' media event is
 * the only thing that writes a `complete` row, and it does not fire if the
 * nurse presses Next on the last few seconds, if the tab is backgrounded at the
 * wrong moment, or if the file's duration metadata is a shade long. Crediting
 * the last 3% costs nothing and stops the course telling someone who watched
 * the whole part that she did not.
 */
const WATCHED_AT = 0.97;

/**
 * Completion state per video for ONE user, from their own rows. Pure.
 * 'watched'  — a complete event exists;
 * 'checked'  — every active question for the video has a `done` answer
 *              (correct, or three attempts used). No questions = checked
 *              the moment it is watched;
 * 'started'  — she has pressed play at least once. Opening a part and closing
 *              it again does NOT count: a screen she merely glanced at must
 *              not read as work begun;
 * 'furthest' — the furthest second reached, so a part left half-way says so
 *              instead of collapsing back to 'Not started';
 * 'percent'  — that position against the part's length (100 once done).
 * @param {any[]} videos
 * @param {any[]} questions
 * @param {any[]} events   the user's own training_events
 * @param {any[]} answers  the user's own training_answers
 * @returns {Record<string, {watched: boolean, checked: boolean, done: boolean, started: boolean, furthest: number, percent: number, questions: number, answered: number}>}
 */
export function progressByVideo(videos, questions, events, answers) {
  /** @type {Record<string, any>} */
  const out = {};
  const watched = new Set();
  const started = new Set();
  const checkDone = new Set();
  /** @type {Map<string, number>} */
  const furthest = new Map();
  for (const e of events || []) {
    if (!e || !e.video_id) continue;
    if (e.event_type === 'complete') watched.add(e.video_id);
    if (e.event_type === 'check_complete') checkDone.add(e.video_id);
    if (STARTED_EVENTS.has(e.event_type)) started.add(e.video_id);
    const at = Math.max(Number(e.position_seconds) || 0, Number(e.to_position) || 0);
    if (at > (furthest.get(e.video_id) || 0)) furthest.set(e.video_id, at);
  }
  for (const v of videos || []) {
    const qs = questionsForVideo(questions, v.video_id);
    const len = Number(v.duration_seconds) || 0;
    const at = furthest.get(v.video_id) || 0;
    // Watched = the media said so, OR she reached the last 3% of it.
    const w = watched.has(v.video_id) || (len > 0 && at >= len * WATCHED_AT);
    const answered = qs.some((q) => (answers || []).some((a) => a.question_id === q.question_id));
    // Checked = every question answered to completion, OR the check reported
    // itself finished. Deriving this from the ANSWER rows alone made a nurse's
    // progress hostage to a best-effort analytics write: one failed insert and
    // a part she finished reads 'Check pending' forever, with no way out.
    const c =
      qs.length === 0
        ? w
        : checkDone.has(v.video_id) ||
          qs.every((q) =>
            (answers || []).some(
              (a) => a.question_id === q.question_id && (a.is_correct || a.attempt_no >= MAX_ATTEMPTS)
            )
          );
    const done = w && c;
    // How many of this part's questions are actually finished. The card used to
    // say only 'Check pending', which told a nurse nothing about WHAT was
    // blocking the part or how far off she was — she could leave a check
    // half-done and never learn why the part would not complete.
    const answeredToCompletion = qs.filter((qq) =>
      (answers || []).some(
        (a) => a.question_id === qq.question_id && (a.is_correct || a.attempt_no >= MAX_ATTEMPTS)
      )
    ).length;
    out[v.video_id] = {
      watched: w,
      checked: c,
      done,
      started: started.has(v.video_id) || answered,
      furthest: at,
      percent: done ? 100 : len ? Math.max(0, Math.min(99, Math.round((at / len) * 100))) : w ? 99 : 0,
      questions: qs.length,
      answered: checkDone.has(v.video_id) ? qs.length : answeredToCompletion,
    };
  }
  return out;
}

// ─── Pure: admin aggregations ──────────────────────────────────────────────

/**
 * Per-nurse training performance. Pure.
 * First-attempt accuracy counts each question ONCE per user (their attempt 1).
 * @param {any[]} events all training_events (admin read)
 * @param {any[]} answers all training_answers (admin read)
 * @param {any[]} videos catalogue
 * @param {Array<{user_id: string, full_name?: string}>} users staff directory
 * @returns {Array<{userId: string, name: string, parts: number, totalParts: number, watchMinutes: number, firstTryPct: number|null, flagged: number, lastActivity: string|null}>}
 */
export function perNurseStats(events, answers, videos, users) {
  const byUser = new Map();
  const rec = (uid) => {
    if (!byUser.has(uid)) {
      byUser.set(uid, { complete: new Set(), seconds: 0, firstTry: 0, firstTotal: 0, flagged: 0, last: null });
    }
    return byUser.get(uid);
  };
  for (const e of events || []) {
    if (!e || !e.user_id) continue;
    const u = rec(e.user_id);
    if (e.event_type === 'complete') u.complete.add(e.video_id);
    if (e.event_type === 'heartbeat') u.seconds += 15; // one heartbeat ≈ 15 s watched
    if (!u.last || String(e.occurred_at) > String(u.last)) u.last = e.occurred_at;
  }
  for (const a of answers || []) {
    if (!a || !a.user_id) continue;
    const u = rec(a.user_id);
    if (Number(a.attempt_no) === 1) {
      u.firstTotal += 1;
      if (a.is_correct) u.firstTry += 1;
    }
    if (!a.is_correct && Number(a.attempt_no) >= MAX_ATTEMPTS) u.flagged += 1;
    if (!u.last || String(a.answered_at) > String(u.last)) u.last = a.answered_at;
  }
  const nameOf = (uid) => {
    const u = (users || []).find((x) => x.user_id === uid);
    return (u && u.full_name) || 'Unknown user';
  };
  const totalParts = (videos || []).length;
  return [...byUser.entries()]
    .map(([userId, u]) => ({
      userId,
      name: nameOf(userId),
      parts: u.complete.size,
      totalParts,
      watchMinutes: Math.round(u.seconds / 60),
      firstTryPct: u.firstTotal ? Math.round((u.firstTry / u.firstTotal) * 100) : null,
      flagged: u.flagged,
      lastActivity: u.last,
    }))
    .sort((a, b) => b.parts - a.parts || a.name.localeCompare(b.name));
}

/** Team first-attempt success threshold below which a question is a WEAK TOPIC. */
export const WEAK_TOPIC_PCT = 60;

/**
 * Per-question difficulty across the team. Pure.
 * A question is `weak` when team first-attempt success < WEAK_TOPIC_PCT or the
 * average attempts used exceeds 2 — the §8 curation-loop signal.
 * @param {any[]} answers all training_answers (admin read)
 * @param {any[]} questions the bank
 * @returns {Array<{questionId: string, prompt: string, videoId: string|null, answered: number, firstTryPct: number|null, avgAttempts: number|null, weak: boolean}>}
 */
export function perQuestionStats(answers, questions) {
  return (questions || [])
    .map((q) => {
      const mine = (answers || []).filter((a) => a && a.question_id === q.question_id);
      const users = [...new Set(mine.map((a) => a.user_id))];
      let firstTry = 0;
      let attemptsSum = 0;
      for (const uid of users) {
        const theirs = mine.filter((a) => a.user_id === uid);
        const first = theirs.find((a) => Number(a.attempt_no) === 1);
        if (first && first.is_correct) firstTry += 1;
        attemptsSum += Math.max(...theirs.map((a) => Number(a.attempt_no) || 1));
      }
      const n = users.length;
      const firstTryPct = n ? Math.round((firstTry / n) * 100) : null;
      const avgAttempts = n ? Math.round((attemptsSum / n) * 10) / 10 : null;
      return {
        questionId: q.question_id,
        prompt: q.prompt,
        videoId: q.video_id || null,
        answered: n,
        firstTryPct,
        avgAttempts,
        weak: n > 0 && ((firstTryPct !== null && firstTryPct < WEAK_TOPIC_PCT) || (avgAttempts !== null && avgAttempts > 2)),
      };
    })
    .filter((row) => row.answered > 0)
    .sort((a, b) => (a.firstTryPct ?? 101) - (b.firstTryPct ?? 101));
}

// ─── Storage URL (public bucket) ───────────────────────────────────────────

/**
 * Public playback URL for a catalogue row. The training bucket is public
 * (created with the 92 pack), so no signing round-trip is needed. In the demo
 * build the base URL is a .invalid host — the <video> error handler shows the
 * honest "file not available in this build" note instead.
 * @param {{storage_path?: string}} video
 * @returns {string|null}
 */
export function videoUrl(video) {
  const p = video && video.storage_path;
  if (!p) return null;
  return `${SUPABASE_URL}/storage/v1/object/public/${BUCKETS.TRAINING}/${encodeURIComponent(p)}`;
}

/**
 * True in the demo build, where creds.stub.js points at a .invalid host and the
 * video therefore CANNOT load. The player says so in those words, instead of
 * blaming the nurse's connection for a file that was never going to arrive.
 */
export const DEMO_BUILD = String(SUPABASE_URL).indexOf('.invalid') !== -1;

// ─── Loaders ───────────────────────────────────────────────────────────────

/**
 * Load the course: catalogue + question bank (everyone) + the signed-in user's
 * own progress rows. Never throws; failure lands as loadError on the slice so
 * the view renders retry — never a false "no training yet".
 * @param {{client?: any, session?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function loadTraining(deps) {
  const client = (deps && deps.client) || getClient();
  const session = (deps && deps.session) || store.get(SLICES.SESSION) || {};
  const uid = session.user && session.user.id;
  const vids = await fetchAll(() => client.from(TABLES.TRAINING_VIDEOS).select('*', { count: 'exact' }).order('ordinal'));
  if (!vids.ok) {
    store.set(SLICES.TRAINING, { videos: [], questions: [], progress: {}, answers: [], loadError: vids.error });
    return fail(vids.error);
  }
  const qs = await fetchAll(() => client.from(TABLES.TRAINING_QUESTIONS).select('*', { count: 'exact' }).order('ordinal'));
  if (!qs.ok) {
    store.set(SLICES.TRAINING, { videos: [], questions: [], progress: {}, answers: [], loadError: qs.error });
    return fail(qs.error);
  }
  // Own progress: best-effort — a nurse whose history read fails still gets the
  // course (her ✓ marks may be stale until retry; the videos must never be).
  let events = [];
  let answers = [];
  if (uid) {
    const ev = await fetchAll(() =>
      client.from(TABLES.TRAINING_EVENTS).select('*', { count: 'exact' }).eq('user_id', uid)
    );
    if (ev.ok) events = ev.data;
    const an = await fetchAll(() =>
      client.from(TABLES.TRAINING_ANSWERS).select('*', { count: 'exact' }).eq('user_id', uid)
    );
    if (an.ok) answers = an.data;
  }
  const videos = cleanVideos(vids.data);
  const questions = cleanQuestions(qs.data);
  const progress = progressByVideo(videos, questions, events, answers);
  store.set(SLICES.TRAINING, {
    videos,
    questions,
    progress,
    answers,
    loadError: null,
  });
  return ok({ videos: videos.length, questions: questions.length });
}

/**
 * Admin-only: everything the Training performance panel needs. Returns the
 * envelope; the caller (Reports) stores it view-locally like the sign-in panel.
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function loadTrainingAdmin(deps) {
  const client = (deps && deps.client) || getClient();
  const vids = await fetchAll(() => client.from(TABLES.TRAINING_VIDEOS).select('*', { count: 'exact' }).order('ordinal'));
  if (!vids.ok) return fail(vids.error);
  const qs = await fetchAll(() => client.from(TABLES.TRAINING_QUESTIONS).select('*', { count: 'exact' }));
  if (!qs.ok) return fail(qs.error);
  const ev = await fetchAll(() => client.from(TABLES.TRAINING_EVENTS).select('*', { count: 'exact' }));
  if (!ev.ok) return fail(ev.error);
  const an = await fetchAll(() => client.from(TABLES.TRAINING_ANSWERS).select('*', { count: 'exact' }));
  if (!an.ok) return fail(an.error);
  return ok({
    videos: cleanVideos(vids.data),
    questions: cleanQuestions(qs.data),
    events: ev.data,
    answers: an.data,
  });
}

// ─── Best-effort writers ───────────────────────────────────────────────────

/**
 * Record one watch event. Best-effort by design: a failed telemetry write is
 * logged to the console and swallowed — it must never interrupt the player.
 * @param {{videoId: string, sessionId: string, type: string, position?: number, from?: number, to?: number}} e
 * @param {{client?: any, session?: any}} [deps]
 * @returns {Promise<void>}
 */
export async function logTrainingEvent(e, deps) {
  try {
    const client = (deps && deps.client) || getClient();
    const session = (deps && deps.session) || store.get(SLICES.SESSION) || {};
    const uid = session.user && session.user.id;
    if (!uid) return;
    const { error } = await client.from(TABLES.TRAINING_EVENTS).insert({
      user_id: uid,
      video_id: e.videoId,
      session_id: e.sessionId,
      event_type: e.type,
      position_seconds: e.position === undefined ? null : e.position,
      from_position: e.from === undefined ? null : e.from,
      to_position: e.to === undefined ? null : e.to,
    });
    if (error) console.warn('training event not recorded', error);
  } catch (err) {
    console.warn('training event not recorded', err);
  }
}

/**
 * Record one answer attempt. Same best-effort contract as logTrainingEvent —
 * but it also updates the slice's local answers so progress marks move even
 * when offline (they re-sync on next load).
 * @param {{questionId: string, attemptNo: number, selectedIndex: number, isCorrect: boolean}} a
 * @param {{client?: any, session?: any}} [deps]
 * @returns {Promise<void>}
 */
export async function recordAnswer(a, deps) {
  const session = (deps && deps.session) || store.get(SLICES.SESSION) || {};
  const uid = session.user && session.user.id;
  const localRow = {
    user_id: uid,
    question_id: a.questionId,
    attempt_no: a.attemptNo,
    selected_index: a.selectedIndex,
    is_correct: a.isCorrect,
    answered_at: new Date().toISOString(),
  };
  const t = store.get(SLICES.TRAINING) || {};
  store.set(SLICES.TRAINING, { answers: (t.answers || []).concat([localRow]) });
  try {
    const client = (deps && deps.client) || getClient();
    if (!uid) return;
    const { error } = await client.from(TABLES.TRAINING_ANSWERS).insert(localRow);
    if (error) console.warn('training answer not recorded', error);
  } catch (err) {
    console.warn('training answer not recorded', err);
  }
}
